import React, { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Collapse from '@mui/material/Collapse'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { currency } from '@/v2/utils/format'
import { formatDate } from '@/v2/utils/dateHelpers'
import { StatusBadge, MutedCell } from '../styles'
import { METHOD_LABELS, PAYMENT_STATUS_LABELS } from '../constants'
import FeeBreakdownPanel, { hasBreakdown } from './FeeBreakdownPanel'

const COLUMN_COUNT = 10

const DEFAULT_BADGE = { backgroundColor: '#eeeeee', color: '#555' }

const METHOD_COLORS = {
  credit_card: { backgroundColor: '#ede7f6', color: '#5e35b1' },
  ach: { backgroundColor: '#e3f2fd', color: '#1565c0' },
}

const PAYMENT_STATUS_COLORS = {
  processed: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
  pending: { backgroundColor: '#fff8e1', color: '#b78103' },
  capturing: { backgroundColor: '#fff8e1', color: '#b78103' },
  declined: { backgroundColor: '#fdecea', color: '#c62828' },
  denied: { backgroundColor: '#fdecea', color: '#c62828' },
  error: { backgroundColor: '#fdecea', color: '#c62828' },
  chargeback: { backgroundColor: '#fdecea', color: '#c62828' },
  refunded: { backgroundColor: '#eeeeee', color: '#555' },
  partially_refunded: { backgroundColor: '#fff3e0', color: '#e65100' },
}

const FeeCell = ({ value }) =>
  Number(value) > 0 ? currency(value) : <MutedCell>{currency(value)}</MutedCell>

const PaymentRow = ({ payment }) => {
  const [expanded, setExpanded] = useState(false)
  const expandable = hasBreakdown(payment)

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 48 }}>
          {expandable ? (
            <IconButton size="small" onClick={() => setExpanded(prev => !prev)}>
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          ) : null}
        </TableCell>
        <TableCell>
          {payment.paidAt ? formatDate(payment.paidAt, 'MMM dd, yyyy') : '—'}
        </TableCell>
        <TableCell>
          {payment.patientId ? (
            <Link
              href={`/v2/patient/${payment.patientId}/finance`}
              color="link.main"
              underline="hover"
            >
              {payment.patientName}
            </Link>
          ) : (
            payment.patientName || <MutedCell>—</MutedCell>
          )}
        </TableCell>
        <TableCell>{payment.contractId ?? '—'}</TableCell>
        <TableCell>
          <StatusBadge
            label={
              METHOD_LABELS[payment.paymentMethod] || payment.paymentMethod
            }
            sx={METHOD_COLORS[payment.paymentMethod] || DEFAULT_BADGE}
            size="small"
          />
        </TableCell>
        <TableCell align="right">{currency(payment.baseAmount)}</TableCell>
        <TableCell align="right">
          <FeeCell value={payment.patientFee} />
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {currency(payment.totalCharged)}
        </TableCell>
        <TableCell>
          <StatusBadge
            label={
              PAYMENT_STATUS_LABELS[payment.paymentStatus] ||
              payment.paymentStatus
            }
            sx={PAYMENT_STATUS_COLORS[payment.paymentStatus] || DEFAULT_BADGE}
            size="small"
          />
        </TableCell>
        <TableCell>
          {payment.receiptUrl ? (
            <Link
              href={payment.receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="link.main"
              sx={{ display: 'inline-flex' }}
            >
              <OpenInNewIcon fontSize="small" />
            </Link>
          ) : (
            <MutedCell>—</MutedCell>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={COLUMN_COUNT} sx={{ padding: 0, borderBottom: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <FeeBreakdownPanel payment={payment} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default PaymentRow
